/**
 * Multi-column sort controls.
 *
 * Click a header → cycle through 'asc' → 'desc' → 'off' for its column.
 * Shift-click adds the column to the secondary sort stack (max 3
 * columns deep — beyond that the array shifts to keep size bounded).
 */
import { ArrowDown, ArrowUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import { HEADER_ROW_HEIGHT, type ListColumnDef, rowTemplateColumns } from './list-columns';
import type { CardListSort } from './list-types';

const MAX_SORT_STACK = 3;

export interface ListSortControlsProps {
  columns: readonly ListColumnDef[];
  sorts: CardListSort[];
  onChange: (sorts: CardListSort[]) => void;
  selectedCount: number;
  totalRowCount: number;
  onToggleSelectAll: () => void;
}

export function ListSortControls({
  columns,
  sorts,
  onChange,
  selectedCount,
  totalRowCount,
  onToggleSelectAll,
}: ListSortControlsProps) {
  const grid = React.useMemo(() => rowTemplateColumns(columns), [columns]);

  function cycle(colId: string, additive: boolean) {
    const existingIdx = sorts.findIndex((s) => s.column === colId);
    let next: CardListSort[];
    if (existingIdx === -1) {
      const entry: CardListSort = {
        column: colId as CardListSort['column'],
        dir: 'asc',
      };
      next = additive ? [...sorts, entry] : [entry];
    } else {
      const existing = sorts[existingIdx];
      if (!existing) {
        next = sorts;
      } else {
        const newDir: CardListSort['dir'] | null = existing.dir === 'asc' ? 'desc' : null;
        next = additive ? [...sorts] : [];
        if (additive) {
          if (newDir === null) {
            next.splice(existingIdx, 1);
          } else {
            next[existingIdx] = { column: existing.column, dir: newDir };
          }
        } else if (newDir !== null) {
          next = [{ column: existing.column, dir: newDir }];
        }
      }
    }
    if (next.length > MAX_SORT_STACK) {
      next = next.slice(next.length - MAX_SORT_STACK);
    }
    onChange(next);
  }

  const allSelected = totalRowCount > 0 && selectedCount === totalRowCount;
  const someSelected = selectedCount > 0 && selectedCount < totalRowCount;

  return (
    <div
      role="row"
      className="sticky top-0 z-10 grid items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur"
      style={{ gridTemplateColumns: `28px ${grid}`, height: HEADER_ROW_HEIGHT }}
    >
      <div className="flex items-center justify-center">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={onToggleSelectAll}
          aria-label={allSelected ? 'Deselect all' : 'Select all'}
          className="h-3.5 w-3.5 rounded border-border accent-foreground"
        />
      </div>
      {columns.map((c) => {
        const idx = sorts.findIndex((s) => s.column === c.id);
        const sort = idx >= 0 ? sorts[idx] : null;
        const arrow = sort?.dir === 'asc' ? ArrowUp : sort?.dir === 'desc' ? ArrowDown : null;
        return (
          <button
            type="button"
            role="columnheader"
            key={c.id}
            onClick={(e) => cycle(c.id, e.shiftKey)}
            className={cn(
              'group flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide',
              'text-muted-foreground transition-colors hover:text-foreground',
            )}
            aria-sort={
              sort?.dir === 'asc' ? 'ascending' : sort?.dir === 'desc' ? 'descending' : 'none'
            }
          >
            <span className="truncate">{c.label}</span>
            {arrow ? (
              <>
                {React.createElement(arrow, {
                  size: 11,
                  className: 'shrink-0 text-foreground',
                  'aria-hidden': true,
                })}
                {sorts.length > 1 && idx >= 0 && (
                  <span className="text-[10px] text-foreground/60">{idx + 1}</span>
                )}
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export { MAX_SORT_STACK };
